import { useId, useState, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { MapPin, MoreHorizontal, Plus, List, ListChecks, Upload, Sparkles, ChevronDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useActiveLocation } from "@/contexts/ActiveLocationContext";
import { AddToDeckDialog } from "./AddToDeckDialog";
import type { CookingSessionSource } from "@/types/cooking-session";

interface WorkingPageHeaderProps {
  tablet?: boolean;
  /**
   * Which board this header sits on (T-1105). Prep and service share the
   * layout and nothing else: the deck belongs to prep, and a header that says
   * "Working" on both leaves no way to tell which one you are looking at.
   */
  source?: CookingSessionSource;
}

interface ComingSoonActionProps {
  variant: "brand-outline" | "default";
  className: string;
  /** What the disabled button would do, and why it cannot yet. Read out by screen readers and shown as a tooltip. */
  reason: string;
  /** Short visible label, so the reason shows on touch where there is no hover. */
  badge: string;
  children: ReactNode;
}

/**
 * YC-29. Export, Build Prep List and Quick AI have no feature behind them in
 * yeschef-api yet, and used to sit here as buttons that did nothing. Until they
 * are built they are disabled and say so: a visible badge for touch, a tooltip
 * for desktop and the reason as the button's accessible description. Drop this
 * wrapper for a plain `Button` when each action lands.
 */
function ComingSoonAction({ variant, className, reason, badge, children }: ComingSoonActionProps) {
  const reasonId = useId();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className="inline-flex items-center gap-1.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2"
          >
            <Button variant={variant} size="sm" className={className} disabled aria-describedby={reasonId}>
              {children}
            </Button>
            <Badge variant="soft" aria-hidden="true">
              {badge}
            </Badge>
            <span id={reasonId} className="sr-only">
              {reason}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function WorkingPageHeader({ tablet = false, source }: WorkingPageHeaderProps) {
  const { t } = useLingui();
  const { user, hasPermission } = useAuth();
  const orgName = user?.organization?.name ?? user?.company?.name;
  const [addToDeckOpen, setAddToDeckOpen] = useState(false);
  const { activeLocation, setActiveLocation, locations } = useActiveLocation();

  const urlLocationId =
    typeof window !== "undefined"
      ? parseInt(new URLSearchParams(window.location.search).get("locationId") ?? "", 10) || undefined
      : undefined;
  const locationId = activeLocation?.id ?? urlLocationId;
  const locationLabel = activeLocation?.name ?? t`All locations`;

  const title = source === "prep" ? t`Prep` : source === "order" ? t`Service` : t`Working`;
  const subtitle =
    source === "prep"
      ? t`What the kitchen is building today, before the tickets start.`
      : t`Track cooks, assign chefs, and keep timing tight.`;
  // The deck queues a recipe against no ticket, which makes it prep by
  // definition — putting the button on the service board would add work the
  // service board then cannot show.
  const canAddToDeck = source !== "order" && hasPermission("cooking-session:create");

  const comingSoon = t`Coming soon`;
  const exportReason = t`Export coming soon`;
  const buildPrepListReason = t`Build Prep List coming soon`;
  const quickAiReason = t`Quick AI coming soon`;

  const locationMenuContent = (
    <DropdownMenuContent align="start" className="min-w-50">
      {orgName && (
        <>
          <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-(--color-text-secondary) font-normal">
            <Building2 className="size-3.5 shrink-0" />
            {orgName}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
        </>
      )}
      <div className="max-h-64 overflow-y-auto">
        <DropdownMenuItem onClick={() => setActiveLocation(null)}>
          {t`All locations`}
        </DropdownMenuItem>
        {locations.map((loc) => (
          <DropdownMenuItem key={loc.id} onClick={() => setActiveLocation(loc)}>
            {loc.name}
          </DropdownMenuItem>
        ))}
      </div>
    </DropdownMenuContent>
  );

  if (tablet) {
    return (
      <>
        <div className="flex items-center gap-3 h-15.5 px-5 border-b border-(--color-border)">
          <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary)">
            {title}
          </h1>
          <Badge
            variant="success"
            className="gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold"
          >
            <span className="size-1.5 rounded-full bg-(--color-success-text) animate-pulse" />
            {t`Live`}
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 px-3 gap-2 rounded-lg border border-(--color-border) bg-(--color-surface-muted) text-[0.8125rem] font-semibold text-(--color-text-primary) hover:bg-(--color-surface-accent)"
              >
                <MapPin className="h-4 w-4 text-(--color-text-secondary)" />
                {locationLabel}
                <ChevronDown className="h-3.5 w-3.5 text-(--color-text-secondary)" />
              </Button>
            </DropdownMenuTrigger>
            {locationMenuContent}
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-2">
            <ComingSoonAction variant="brand-outline" className="h-11 gap-2" reason={exportReason} badge={comingSoon}>
              <Upload className="h-4 w-4" />
              {t`Export`}
            </ComingSoonAction>
            <ComingSoonAction variant="brand-outline" className="h-11 gap-2" reason={buildPrepListReason} badge={comingSoon}>
              <ListChecks className="h-4 w-4" />
              {t`Build Prep List`}
            </ComingSoonAction>
            {canAddToDeck && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={!locationId ? 0 : undefined}
                      className="inline-flex rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2"
                    >
                      <Button
                        variant="brand-outline"
                        size="sm"
                        className="h-11 gap-2"
                        disabled={!locationId}
                        onClick={() => setAddToDeckOpen(true)}
                      >
                        <Plus className="h-4 w-4" />
                        {t`Add to Deck`}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!locationId && (
                    <TooltipContent>{t`Select a location first`}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <ComingSoonAction variant="default" className="h-11 gap-2" reason={quickAiReason} badge={comingSoon}>
              <Sparkles className="h-4 w-4" />
              {t`Quick AI`}
            </ComingSoonAction>
          </div>
        </div>

        <AddToDeckDialog
          open={addToDeckOpen}
          onOpenChange={setAddToDeckOpen}
          locationId={locationId}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 px-3 pt-5 pb-4 border-b border-(--color-border)">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-4xl font-semibold text-(--color-text-primary)">{title}</h1>
              <Badge className="bg-(--color-surface-chip-inactive) text-(--color-text-primary) hover:bg-(--color-surface-chip-inactive) border-none rounded-full px-3 py-0.5 text-sm font-medium">
                {t`Live`}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-(--color-text-secondary)">
              {subtitle}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ComingSoonAction variant="brand-outline" className="h-13 px-5 gap-2" reason={exportReason} badge={comingSoon}>
              {t`Export`}
              <MoreHorizontal className="h-4 w-4" />
            </ComingSoonAction>
            {canAddToDeck && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={!locationId ? 0 : undefined}
                      className="inline-flex rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2"
                    >
                      <Button
                        variant="default"
                        size="sm"
                        className="h-13 px-5 gap-2"
                        disabled={!locationId}
                        onClick={() => setAddToDeckOpen(true)}
                      >
                        {t`Add to Deck`}
                        <Plus className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!locationId && (
                    <TooltipContent>
                      {t`Select a location first`}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-11 px-4 gap-2 rounded-lg text-sm text-(--color-text-primary)"
              >
                <span className="text-(--color-text-secondary) text-xs font-medium">{t`Location`}</span>
                <span className="w-px h-4 bg-border" />
                <MapPin className="h-4 w-4 text-(--color-text-secondary)" />
                <span className="font-medium">{locationLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 text-(--color-text-secondary)" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-50">
              {orgName && (
                <>
                  <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-(--color-text-secondary) font-normal">
                    <Building2 className="size-3.5 shrink-0" />
                    {orgName}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              <div className="max-h-64 overflow-y-auto">
                <DropdownMenuItem onClick={() => setActiveLocation(null)}>
                  {t`All locations`}
                </DropdownMenuItem>
                {locations.map((loc) => (
                  <DropdownMenuItem
                    key={loc.id}
                    onClick={() => setActiveLocation(loc)}
                  >
                    {loc.name}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-2">
            <ComingSoonAction variant="brand-outline" className="h-11 gap-2" reason={buildPrepListReason} badge={comingSoon}>
              <List className="h-4 w-4" />
              {t`Build Prep List`}
            </ComingSoonAction>
            <ComingSoonAction variant="default" className="h-11 gap-2" reason={quickAiReason} badge={comingSoon}>
              <Sparkles className="h-4 w-4" />
              {t`Quick AI`}
            </ComingSoonAction>
          </div>
        </div>
      </div>

      <AddToDeckDialog
        open={addToDeckOpen}
        onOpenChange={setAddToDeckOpen}
        locationId={locationId}
      />
    </>
  );
}
